/**
 * Shared instant-timestamp formatter for 21 CFR Part 11 records (A2F-1019).
 *
 * Canonical format:  YYYY-MM-DD HH:MM ABBREV (UTC±HH:MM)
 * With seconds=true: YYYY-MM-DD HH:MM:SS ABBREV (UTC±HH:MM)
 * With dateOnly=true: YYYY-MM-DD  (no time, no label — for timeAgo fallback)
 *
 * Uses U+2212 MINUS SIGN (−) for negative offsets to match backend.
 * DST-correct: abbrev and offset reflect the instant's own date.
 * Unset/null/undefined tz → 'UTC' (labeled, never silent).
 * Invalid ISO string → returned verbatim (don't throw).
 */

/**
 * Format a UTC ISO-8601 instant string in the given IANA timezone.
 *
 * @param iso   ISO-8601 string with offset (e.g. "2026-06-01T21:03:00+00:00")
 * @param tz    IANA timezone string (e.g. "America/Los_Angeles") or null/undefined → UTC
 * @param opts  { seconds?: boolean; dateOnly?: boolean }
 */
export function formatInstant(
  iso: string,
  tz: string | null | undefined,
  opts?: { seconds?: boolean; dateOnly?: boolean },
): string {
  if (!iso) return iso;

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    // Invalid ISO string — return verbatim rather than throw
    return iso;
  }

  const zone = tz || 'UTC';

  // --- Date-only mode (for timeAgo fallback) ---
  if (opts?.dateOnly) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  }

  // --- Full instant mode ---

  // 1. Date + time stamp via en-CA (ISO-style YYYY-MM-DD) with 24-hour clock
  const dtfOpts: Intl.DateTimeFormatOptions = {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  if (opts?.seconds) dtfOpts.second = '2-digit';

  const dateParts = new Intl.DateTimeFormat('en-CA', dtfOpts).formatToParts(date);
  const get = (t: string) => dateParts.find(p => p.type === t)?.value ?? '';
  const stamp = opts?.seconds
    ? `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`
    : `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;

  // 2. Timezone abbreviation (e.g. "PDT", "UTC")
  const abbrevParts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'short',
  }).formatToParts(date);
  const abbrev = abbrevParts.find(p => p.type === 'timeZoneName')?.value ?? '';

  // 3. UTC offset in (UTC±HH:MM) notation
  //    longOffset gives "GMT-07:00", "GMT+05:30", or just "GMT" for UTC
  const offsetParts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'longOffset',
  }).formatToParts(date);
  const rawOffset = offsetParts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT';

  let offsetLabel: string;
  if (rawOffset === 'GMT' || rawOffset === 'UTC') {
    offsetLabel = '(UTC+00:00)';
  } else {
    // "GMT-07:00" → "UTC−07:00"  (replace "GMT" with "UTC", ASCII minus → U+2212)
    offsetLabel = `(${rawOffset.replace('GMT', 'UTC').replace('UTC-', 'UTC−')})`;
  }

  return `${stamp} ${abbrev} ${offsetLabel}`;
}
